import { Map, OrderedSet, Set } from 'immutable';

import { NoResourceError, NotebookCellRunResource } from '@mode/legacy/data-access-model-js';
import { AppState, NormalizedNotebookCellRunArray } from '@mode/legacy/types-app-state';
import { ReducerTree } from '@mode/legacy/util-domain';
import { Actions } from '../../../actions/index';
import { CellRunActions } from '../../../actions/notebooks/cell-run.actions';
import { CellActions } from '../../../actions/notebooks/cell.actions';
import { NotebookActions } from '../../../actions/notebooks/notebook.actions';

export const initialCellRunsState: NormalizedNotebookCellRunArray = {
  tokens: OrderedSet<string>(),
  resources: Map<string, NotebookCellRunResource>(),
  executionCounts: Map<string, number>(),
  resultTokens: Map<string, OrderedSet<string>>(),
  missingResults: Set<string>(),
};

function tokensReducer(state = initialCellRunsState.tokens, action: Actions): NormalizedNotebookCellRunArray['tokens'] {
  switch (action.type) {
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.runToken);
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellRunActions.Type.LoadCellRunFromBatchSuccess: {
      return state.remove(action.runToken).add(action.resource.token);
    }
    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      return state.remove(action.runToken).add(action.resource.token);
    }
  }

  return state;
}

function resourcesReducer(
  state = initialCellRunsState.resources,
  action: Actions
): NormalizedNotebookCellRunArray['resources'] {
  switch (action.type) {
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.runToken);
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellRunActions.Type.LoadCellRunFromBatchSuccess: {
      return state.remove(action.runToken).set(action.resource.token, action.resource);
    }
    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      return state.remove(action.runToken).set(action.resource.token, action.resource);
    }
  }

  return state;
}

function executionCountsReducer(
  state = initialCellRunsState.executionCounts,
  action: Actions
): NormalizedNotebookCellRunArray['executionCounts'] {
  switch (action.type) {
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.runToken);
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellRunActions.Type.LoadCellRunFromBatchSuccess: {
      return state.remove(action.runToken).set(action.resource.token, action.resource.execution_count);
    }
    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      return state.remove(action.runToken).set(action.resource.token, action.resource.execution_count);
    }
  }

  return state;
}

function resultTokensReducer(
  state = initialCellRunsState.resultTokens,
  action: Actions
): NormalizedNotebookCellRunArray['resultTokens'] {
  switch (action.type) {
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellActions.Type.DestroyCellSuccess: {
      return state.remove(action.runToken);
    }
    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      return state.remove(action.runToken);
    }
    case CellRunActions.Type.LoadCellRunResultsSuccess:
      return state.set(action.runToken, OrderedSet(action.resultResources.map((res) => res.token)));
    case CellRunActions.Type.LoadCellRunResultSuccess:
      // Cell Run may not exist yet. So create a set for it in that case
      if (state.has(action.runToken)) {
        return state.set(action.runToken, state.get(action.runToken).add(action.resultResource.token));
      }

      return state.set(action.runToken, OrderedSet([action.resultResource.token]));
    case NotebookActions.Type.CreateRunSuccess: {
      return state.filterNot((_, runToken) => runToken === action.runToken);
    }
  }

  return state;
}

function missingResultsReducer(
  state = initialCellRunsState.missingResults,
  action: Actions
): NormalizedNotebookCellRunArray['missingResults'] {
  switch (action.type) {
    case CellRunActions.Type.LoadCellRunResultsFailure:
      if (action.error instanceof NoResourceError) {
        return state.add(action.runToken);
      }
      break;
  }

  return state;
}

export const cellRunsReducer: ReducerTree<AppState, NormalizedNotebookCellRunArray, Actions> = {
  tokens: tokensReducer,
  resources: resourcesReducer,
  executionCounts: executionCountsReducer,
  resultTokens: resultTokensReducer,
  missingResults: missingResultsReducer,
};
